import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { CATEGORY_META, REGION_LABELS, statusesFor } from "../../lib/options";
import { formatDate, formatValue, labelize } from "../../lib/format";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { StatusBadge } from "./SubmissionTable";
import { CandidatePanel, CopyButton } from "./CandidatePanel";

const Row = ({ k, v }) => (
  <div className="grid grid-cols-5 gap-3 py-2 border-b border-[#E5E0D8] last:border-0 text-sm">
    <dt className="col-span-2 text-[#5A6561]">{k}</dt>
    <dd className="col-span-3 text-[#1C2421] break-words whitespace-pre-wrap">{v}</dd>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#B25B41]">{title}</h3>
    {children}
  </div>
);

const Notes = ({ doc, onSaved }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${doc.id}/notes`, { text });
      setText("");
      onSaved(data);
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Internal notes">
      <div className="space-y-3" data-testid="admin-notes-list">
        {doc.notes.length === 0 && <p className="text-sm text-[#5A6561]">No notes yet.</p>}
        {doc.notes.map((n) => (
          <div key={n.id} className="rounded-xl bg-[#FAF8F5] border border-[#E5E0D8] p-3">
            <p className="text-sm whitespace-pre-wrap">{n.text}</p>
            <p className="text-[11px] text-[#5A6561] mt-1">{n.author} · {formatDate(n.created_at)}</p>
          </div>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Add an internal note (visible to administrators only)" className="bg-white" data-testid={`admin-note-input-${doc.id}`} />
      <button type="button" onClick={save} disabled={busy || !text.trim()} className="btn-pill btn-primary !py-2 disabled:opacity-50" data-testid={`admin-save-note-btn-${doc.id}`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save note
      </button>
    </Section>
  );
};

const Actions = ({ doc, update }) => {
  const housing = ["housing", "residential_support"].includes(doc.category);
  return (
    <div className="rounded-2xl bg-[#F3EFEA] p-4 space-y-4">
      <div className="space-y-2">
        <label htmlFor="detail-status" className="text-sm font-semibold">{doc.category === "caregiver_application" ? "Applicant stage" : "Follow-up status"}</label>
        <select id="detail-status" value={doc.status} onChange={(e) => update({ status: e.target.value })} className="field-select" data-testid={`admin-status-select-${doc.id}`}>
          {statusesFor(doc.category).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {housing && (
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="detail-interest" className="text-sm"><span className="font-semibold block">Interest list</span><span className="text-xs text-[#5A6561]">Private; never shown publicly.</span></label>
          <Switch id="detail-interest" checked={!!doc.interest_list} onCheckedChange={(v) => update({ interest_list: v })} data-testid={`admin-interest-switch-${doc.id}`} />
        </div>
      )}
    </div>
  );
};

const DeleteButton = ({ doc, onDeleted }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button type="button" className="btn-pill border border-red-200 text-red-700 hover:bg-red-50 !py-2" data-testid={`admin-delete-btn-${doc.id}`}><Trash2 className="h-4 w-4" /> Delete record</button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {doc.reference}?</AlertDialogTitle>
        <AlertDialogDescription>This permanently removes the submission, its notes and consent record, for example to fulfil a deletion request. This cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onDeleted} className="bg-red-700 hover:bg-red-800" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const SubmissionDetail = ({ id, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    setDoc(null);
    if (!id) return;
    api.get(`/admin/submissions/${id}`).then((r) => setDoc(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  const applied = (data) => { setDoc(data); onChanged(); };
  const update = async (body) => {
    try {
      const { data } = await api.patch(`/admin/submissions/${id}`, body);
      applied(data);
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/submissions/${id}`);
      toast.success("Record deleted");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const openResume = async () => {
    try {
      const res = await api.get(`/admin/submissions/${id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.resume.content_type }));
      if (doc.resume.content_type === "application/pdf") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.resume.filename;
        a.click();
      }
    } catch (e) {
      toast.error("Could not open resume");
    }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-white" data-testid="admin-detail-panel">
        {!doc ? (
          <div className="py-20 text-center text-sm text-[#5A6561]"><SheetTitle className="sr-only">Loading</SheetTitle>Loading…</div>
        ) : (
          <div className="space-y-7 pt-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#B25B41]">{CATEGORY_META[doc.category]?.label}</p>
              <SheetTitle className="font-display text-2xl mt-1" data-testid="admin-detail-name">{doc.name}</SheetTitle>
              <SheetDescription className="flex items-center gap-2 mt-2">
                <span className="font-mono text-xs" data-testid="admin-detail-reference">{doc.reference}</span><StatusBadge status={doc.status} />
              </SheetDescription>
            </div>
            <Actions doc={doc} update={update} />
            {doc.resume && (
              <button type="button" onClick={openResume} className="btn-pill btn-primary !py-2" data-testid={`admin-resume-download-${doc.id}`}>
                <Download className="h-4 w-4" /> Open resume ({doc.resume.filename})
              </button>
            )}
            {doc.category === "caregiver_application" && <CandidatePanel doc={doc} onUpdated={applied} />}
            <Section title="Contact">
              <div className="flex flex-wrap gap-2">
                <CopyButton text={doc.email} label="Copy email" testId="admin-copy-email" />
                {doc.phone && <CopyButton text={doc.phone} label="Copy phone" testId="admin-copy-phone" />}
                <CopyButton text={[doc.name, doc.email, doc.phone, doc.answers.organization, doc.answers.preferred_role].filter(Boolean).join("\n")} label="Copy all contact details" testId="admin-copy-all" />
              </div>
              <dl>
                <Row k="Email" v={doc.email} /><Row k="Phone" v={doc.phone || "—"} /><Row k="Preferred contact" v={doc.preferred_contact || "—"} />
                <Row k="Location" v={`${doc.city || "—"}, ${REGION_LABELS[doc.region]}`} /><Row k="Received" v={formatDate(doc.created_at)} />
              </dl>
            </Section>
            <Section title="Submission details">
              <dl data-testid="admin-detail-answers">{Object.entries(doc.answers).map(([k, v]) => <Row key={k} k={labelize(k)} v={formatValue(v)} />)}</dl>
            </Section>
            <Section title="Consent record">
              <dl>
                <Row k="Privacy notice" v={`Accepted (version ${doc.consent.notice_version})`} />
                <Row k="Marketing updates" v={doc.consent.marketing ? "Opted in" : "Not opted in"} />
                {doc.consent.contact_for_employment && <Row k="Employment contact" v="Consented" />}
                <Row k="Admin email notification" v={doc.notification} />
              </dl>
            </Section>
            <Notes doc={doc} onSaved={applied} />
            <Section title="Activity">
              <ul className="space-y-1 text-xs text-[#5A6561]">{doc.history.map((h, i) => <li key={i}>{formatDate(h.at)} · {h.action} · {h.by}</li>)}</ul>
            </Section>
            <DeleteButton doc={doc} onDeleted={remove} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
